import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    DialogTitle,
    DialogContent,
    DialogContentText,
} from '@material-ui/core';
import { toastr } from 'react-redux-toastr';
import { cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Table, EmptyTableText } from '@commonClient2/components';
import {
    UPDATE_AWARDING_RULE, RESET_AWARDING_RULE, deleteAwardingRule,
} from '../store/awardingRule';
import { fetchAdminAppData } from '../store/appData';
import { AdminSidePanelAction } from '../../Enums';
import AwardingRuleEdit from './AwardingRuleEdit';
import './AwardingRules.scss';

function AwardingRules() {
    const dispatch = useDispatch();
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'admin' });
    const { t: tCommon } = useTranslation('common');

    /** @type {ShiftBidding.AdminStore} */
    // @ts-ignore
    const { adminAppData } = useSelector((state) => state);
    const { awardingRules, isAdminAppDataLoaded } = adminAppData;

    const [adminAwardingRules, setAdminAwardingRules] = useState(awardingRules);
    const [sidePanelOpen, setSidePanelOpen] = useState(false);

    useEffect(() => {
        if (!isAdminAppDataLoaded) {
            dispatch(fetchAdminAppData());
        }
    }, []);

    useEffect(() => {
        setAdminAwardingRules(awardingRules);
    }, [JSON.stringify(awardingRules)]);

    /**
     * @param {AwardingRule.IAwardingRuleBrief} rowData
     */
    const handleEdit = (rowData) => {
        const selectedRuleSet = awardingRules.find((x) => x.ruleSetId === rowData.ruleSetId);
        dispatch({
            type: UPDATE_AWARDING_RULE,
            payload: {
                ...selectedRuleSet,
                adminSidePanelAction: AdminSidePanelAction.Edit,
            },
        });
        setSidePanelOpen(true);
    };

    /**
     * @param {AwardingRule.IAwardingRuleBrief} rowData
     */
    const handleDuplicate = (rowData) => {
        const foundSelectedRuleSet = awardingRules.find((x) => x.ruleSetId === rowData.ruleSetId);
        dispatch({
            type: UPDATE_AWARDING_RULE,
            payload: {
                ...cloneDeep(foundSelectedRuleSet),
                ruleSetId: -1,
                name: `${tCommon('copyOf')} ${rowData.name}`,
                isUsed: false,
                adminSidePanelAction: AdminSidePanelAction.Duplicate,
            },
        });
        setSidePanelOpen(true);
    };

    /**
     * @param {AwardingRule.IAwardingRuleBrief} rowData
     */
    const handleDelete = (rowData) => {
        const toastrConfirmOptions = {
            onOk: () => {
                dispatch(deleteAwardingRule(rowData.ruleSetId, { awardingRules }));
            },
            onCancel: () => { },
            component: () => (
                <div>
                    <DialogTitle style={{ padding: '16px 24px' }}>
                        {t('awardingRules.delete.title')}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {t('awardingRules.delete.contentText', { name: rowData.name })}
                        </DialogContentText>
                    </DialogContent>
                </div>
            ),
        };
        toastr.confirm('', toastrConfirmOptions);
    };

    const handleCreateNewBid = () => {
        dispatch({
            type: RESET_AWARDING_RULE,
            payload: {},
        });
        dispatch({
            type: UPDATE_AWARDING_RULE,
            payload: { adminSidePanelAction: AdminSidePanelAction.Create },
        });
        setSidePanelOpen(true);
    };

    const columns = [
        {
            key: 'name',
            tKey: 'columns.awardingRules.name',
            sortable: true,
            flexGrow: 1,
        },
        {
            key: 'description',
            tKey: 'columns.awardingRules.description',
            sortable: true,
            flexGrow: 3,
        },
    ];

    return (
        <div className="ar-full-page">
            <Table
                loading={!isAdminAppDataLoaded}
                data={adminAwardingRules}
                columns={columns}
                id="awarding-rules-table"
                renderEmpty={() => <EmptyTableText text={t('common.noAwardingRuleSetFound')} />}
                onEdit={(_, value) => handleEdit(value)}
                onCreate={handleCreateNewBid}
                createLabel={t('common.newRuleSet')}
                onDuplicate={(_, value) => handleDuplicate(value)}
                onDelete={(_, value) => handleDelete(value)}
                fillAvailableHeight
                defaultSortColumnKey="name"
            />
            <AwardingRuleEdit
                open={sidePanelOpen}
                setOpen={setSidePanelOpen}
            />
        </div>
    );
}
export default AwardingRules;
